import { useState, useRef } from "react"
import PropTypes from "prop-types"
import compact from "lodash/compact"

import AppletConfiguration, { appletConfigShape } from "./applet_configuration"
import Form from "app/components/sdk/form"
import LimitedTextInput from "shared/limited_text_input"
import ErrorReader from "../static_applet_composer/lib/error_reader"

import { arrayAdd, arrayReplace } from "shared/lib/utils"

import "./applet_configuration.scss"

const AppletConfigurations = ({
  configurations,
  onAppletConfigsChange,
  availableTandas,
  icons,
  errors,
  appletUpdating,
  urls,
}) => {
  const formRef = useRef(null)
  const clearedConfigs = configurations.filter(c => c.removed !== true)

  const customFieldNames = compact(
    clearedConfigs.reduce(
      (acc, cur) => acc.concat(cur.customFields.map(c => c.name)),
      []
    )
  )

  const [configTitle, updateConfigTitle] = useState("")

  const handleAppletConfigAddClick = e => {
    e.preventDefault()

    if (!formRef.current.reportValidity()) return
    if (configTitle.trim() === "") return
    updateConfigTitle("")

    onAppletConfigsChange(
      arrayAdd(configurations, {
        title: configTitle.trim(),
        description: "",
        icon: icons[0] || "",
        required: true,
        triggers: [],
        queries: [],
        actions: [],
        customFields: [],
        removed: false,
        status: "valid",
      })
    )
  }

  const handleAppletConfigRemove = index => {
    const updatedAppletConfig = { ...configurations[index], removed: true }
    return () =>
      onAppletConfigsChange(
        arrayReplace(configurations, index, updatedAppletConfig)
      )
  }

  const handleAppletConfigChange = index => {
    return updatedAppletConfig => {
      onAppletConfigsChange(
        arrayReplace(configurations, index, {
          ...updatedAppletConfig,
          status: "validating",
        }),
        index
      )
    }
  }

  const findFirstNotRemoved = () => {
    // this is a hack to remove CSS margins from siblings that will
    // be displayed after a hidden one
    return configurations.findIndex(c => c.removed !== true) + 1
  }

  const allRemoved = () => {
    return configurations.every(c => c.removed === true)
  }

  return (
    <div styleName="features-container">
      <div styleName="add-feature-container">
        <div styleName="features-cta">Add new features</div>
        <Form formRef={formRef} onSubmit={handleAppletConfigAddClick}>
          <span styleName="add-feature">
            <div className="field">
              <LimitedTextInput
                externallyControlledValue={configTitle}
                onChange={updateConfigTitle}
                placeholder="Feature title"
                minLength={5}
                limit={100}
                multiline={false}
                counterPosition="top"
                hardLimit
              />
            </div>
            <button
              styleName="add-button"
              className="button-tertiary"
              onClick={handleAppletConfigAddClick}
              disabled={configTitle.trim() === "" || appletUpdating}
            >
              Add
            </button>
          </span>
        </Form>
        <p styleName="note">
          Features let you define what this connection will do for your users.
          Use features to give your users more fine-grained control and
          visibility.
        </p>
      </div>
      {configurations.length && !allRemoved() ? (
        <div styleName="created-features">
          {configurations.map((config, i) => {
            const configErrors = new ErrorReader(errors).select(
              `applet_configurations/${i}/`
            )
            return (
              <AppletConfiguration
                key={i}
                oneBasedIndex={i + 1}
                display={config.removed !== true}
                config={config}
                firstNotHidden={findFirstNotRemoved()}
                onConfigChange={handleAppletConfigChange(i)}
                onConfigRemove={handleAppletConfigRemove(i)}
                availableTandas={availableTandas}
                customFieldNames={customFieldNames}
                published={false}
                icons={icons}
                errors={configErrors}
                appletUpdating={appletUpdating}
                docs_url={urls.docs_url}
              />
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

AppletConfigurations.propTypes = {
  configurations: PropTypes.arrayOf(appletConfigShape),
  onAppletConfigsChange: PropTypes.func.isRequired,
  availableTandas: PropTypes.object,
  icons: PropTypes.array,
  errors: PropTypes.object,
  appletUpdating: PropTypes.bool.isRequired,
  urls: PropTypes.shape({
    docs_url: PropTypes.string.isRequired,
  }),
}

export default AppletConfigurations
